/* eslint-disable no-console */
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import { parseUserAgent } from "react-device-detect";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  courses as courseQuerys,
  completions as completionQuerys,
  progress as progressQuerys,
  roleplays as roleplayQuerys,
  bots as botQuerys,
  users as userQuerys,
  classrooms as classroomQuerys,
  assignments as assignmentQuerys,
  avatars as avatarQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useCourseState from "@nualang/nualang-ui-components/dist/hooks/useCourseState";
import ViewTopicScreen from "../../../../components/screens/Courses/ViewTopicScreen";
import useServices from "../../../../hooks/useServices";
import useAudioPlayer from "../../../../hooks/useAudioPlayer";
import {
  UploadPhraseAudio,
  UploadExerciseAudioAttempt,
  UploadReportProblemAudio,
} from "../../../../utils/audioUpload";
import SelectClassroom from "@nualang/nualang-ui-components/dist/Dialogs/SelectClassroom";
import {
  createProgress as createProgressAPI,
  deleteCourseSectionTopicProgress as deleteCourseSectionTopicProgressAPI,
  getCourseSectionTopicProgress,
} from "@nualang/nualang-api-and-queries/APIs/Progress";
import {
  createCompletion as createCompletionAPI,
  getCourseSectionTopicCompletions,
} from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getAssignment } from "@nualang/nualang-api-and-queries/APIs/Assignments";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import {
  UploadRoleplayImage,
  UploadBotImage,
  UploadPhraseImage,
  UploadTopicImage,
  UploadBotExerciseImage,
  UploadReportProblemImage,
  UploadPhraseListImage,
} from "../../../../utils/imageUpload";
import {
  highlightSpeechText,
  trackRecommendedEvent,
  searchStringToObj,
} from "../../../../utils/index";
import {
  getClassrooms,
  getClassroom,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { avatars as getAvatars } from "@nualang/nualang-api-and-queries/APIs/Services";

import { getClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";

// components
import TopicImage from "../../../../img/course.svg";

// utils
import config from "../../../../config";
import {
  messageBot,
  getBots,
  updateBot as updateBotAPI,
  deleteBotFromCourse as deleteBotAPI,
} from "@nualang/nualang-api-and-queries/APIs/Bots";
import { getRoleplays } from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import {
  memoizedGetUser,
} from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  updateRoleplay as updateRoleplayAPI,
  deleteRoleplayFromCourse as deleteRoleplayAPI,
} from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import {
  getCourse,
  deleteVideo,
  getSignedUploadUrl,
  uploadVideo,
  updateCourse as updateCourseAPI,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  createCourseMember,
  deleteCourseMember,
  getCourseMember,
  getCourseMembers,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseSectionTopic,
  updateCourseSectionTopic as updateCourseSectionTopicAPI,
  deleteCourseSectionTopic as deleteCourseSectionTopicAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  createCourseSectionTopicPhrase as createCourseSectionTopicPhraseAPI,
  updateCourseSectionTopicPhrase as updateCourseSectionTopicPhraseAPI,
  deleteCourseSectionTopicPhrase as deleteCourseSectionTopicPhraseAPI,
  updateCourseSectionTopicPhrase,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhrases";
import {
  createCourseSectionTopicPhraseList as createCourseSectionTopicPhraseListAPI,
  updateCourseSectionTopicPhraseList as updateCourseSectionTopicPhraseListAPI,
  deleteCourseSectionTopicPhraseList as deleteCourseSectionTopicPhraseListAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  progress as progressHelpers,
  roleplays as roleplayHelpers,
  bots as botHelpers,
  classrooms as classroomHelpers,
  notifications as notificationHelpers,
} from "../../../../utils/helpers";
import {
  validateSpeechRecognition,
  makeChatGptApiRequest,
} from "@nualang/nualang-api-and-queries/APIs/Services";
import { removeExtraWhiteSpaces } from "@nualang/nualang-ui-components/dist/utils";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { utf8ToB64, getForLangCharacters } from "../../../../utils/index";
import uploadPDF from "../../../../utils/pdfUpload";
import { splitFileName } from "../../../../utils/fileName";
import { deleteVectorStore } from "../../../../utils/api/CandooAI/apis/AiQuery";

import logger from "../../../../logger";
import useClassroomLiveGame from "../../../../hooks/useClassroomLiveGame.js";
import { useApp, useSubscription, useCourse } from "../../../../hooks";

const { stage, AssetsURL } = config;

// The RiveScript bot instance, on the window scope so it can be debugged.
window.rivescript = null;

const updateCourseSectionTopicPhrases = async (
  courseId,
  sectionId,
  topicId,
  phrases,
) => {
  return Promise.all(
    phrases.map(async (phrase, i) => {
      await updateCourseSectionTopicPhraseAPI(
        courseId,
        sectionId,
        topicId,
        phrase.phraseId,
        {
          idx: i,
        },
      );
    }),
  );
};

const updateCourseSectionTopicRoleplays = async (roleplays) => {
  return Promise.all(
    roleplays.map(async (roleplay, i) => {
      await updateRoleplayAPI(roleplay.roleplayId, {
        idx: i,
      });
    }),
  );
};

const updateCourseSectionTopicBots = async (bots) => {
  return Promise.all(
    bots.map(async (bot, i) => {
      await updateBotAPI(bot.botId, {
        idx: i,
      });
    }),
  );
};

export function useUpdateCourseSectionTopicPhrases(func, username) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(
        variables.courseId,
        variables.sectionId,
        variables.topicId,
        variables.phrases,
      );
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        courseQuerys.topicKeys.item(
          variables.courseId,
          variables.sectionId,
          variables.topicId,
          username,
        ),
        (old) => {
          if (!old) {
            return old;
          }
          const newPhrases = [...variables.phrases];
          return {
            ...old,
            phrases: newPhrases,
          };
        },
      );
    },
  });
}

export function useUpdateCourseSectionTopicRoleplays(func, username) {
  const { stopLoading, openSnackbar } = useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(variables.roleplays);
    },
    onSettled: () => {
      stopLoading();
    },
    onError: (error) => {
      // An error happened!
      openSnackbar("problem", "error");
      logger.error(error);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        courseQuerys.topicKeys.item(
          variables.courseId,
          variables.sectionId,
          variables.topicId,
          username,
        ),
        (old) => {
          if (!old) {
            return old;
          }
          const newRoleplays = [...variables.roleplays];
          const roleplayIndex = newRoleplays.findIndex(
            (roleplay) => roleplay.roleplayId === variables.roleplayId,
          );
          if (roleplayIndex > -1) {
            newRoleplays[roleplayIndex] = {
              ...newRoleplays[roleplayIndex],
              ...data.Attributes,
            };
          }
          return {
            ...old,
            roleplays: newRoleplays,
          };
        },
      );
      invalidateCache(DataType.ROLEPLAY_ARRAY, {
        courseId: variables.courseId,
        sectionId: variables.sectionId,
        topicId: variables.topicId,
      });
      openSnackbar("updated", "success");
    },
  });
}

export function useUpdateCourseSectionTopicBots(func, username) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(variables.bots);
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        courseQuerys.topicKeys.item(
          variables.courseId,
          variables.sectionId,
          variables.topicId,
          username,
        ),
        (old) => {
          if (!old) {
            return old;
          }
          const newBots = [...variables.bots];
          const botIndex = newBots.findIndex(
            (bot) => bot.botId === variables.botId,
          );
          if (botIndex > -1) {
            newBots[botIndex] = {
              ...newBots[botIndex],
              ...data.Attributes,
            };
          }
          return {
            ...old,
            bots: newBots,
          };
        },
      );
    },
  });
}

const spreadsheetSaveChanges = async (
  courseId,
  sectionId,
  topicId,
  oldPhrases = [],
  newPhrases = [],
) => {
  try {
    // Filters spreadsheet into phrases to create/delete/update
    const createPhrases = newPhrases.filter((p) => !p.phraseId);
    const updatePhrases = newPhrases.filter((p) => p.phraseId);
    const deletePhrases = oldPhrases.filter((p) => {
      const found = newPhrases.find((phrase) => phrase.phraseId === p.phraseId);
      return !found;
    });

    let createCalls = Promise.all(
      createPhrases.map((p) => {
        return createCourseSectionTopicPhraseAPI(
          courseId,
          sectionId,
          topicId,
          p,
        );
      }),
    );

    let deleteCalls = Promise.all(
      deletePhrases.map((p) => {
        return deleteCourseSectionTopicPhraseAPI(
          courseId,
          sectionId,
          topicId,
          p.phraseId,
        );
      }),
    );

    const updatePhraseAttributesToRemove = [
      "courseSectionTopicId",
      "createdAt",
      "createdBy",
      "createdByName",
      "phraseId",
      "updatedAt",
    ];

    let updateCalls = Promise.all(
      updatePhrases.map((p) => {
        let phrase = { ...p };
        updatePhraseAttributesToRemove.forEach((a) => delete phrase[a]);

        return updateCourseSectionTopicPhrase(
          courseId,
          sectionId,
          topicId,
          p.phraseId,
          phrase,
        );
      }),
    );

    const responses = await Promise.all([
      createCalls,
      deleteCalls,
      updateCalls,
    ]);

    await invalidateCache(DataType.PHRASE_SPREADSHEET_UPDATE, {
      courseId,
      sectionId,
      topicId,
    });

    const createResponses = responses[0];
    let phrasesFormatted = newPhrases.map((p) => {
      if (!p.phraseId) {
        const foundPhrase = createResponses.find((cr) => cr.Item.idx === p.idx);

        return {
          ...foundPhrase.Item,
          ...p,
        };
      }
      return p;
    });

    return phrasesFormatted;
  } catch (e) {
    logger.error(e);
  }
};

export function useSpreadsheetSaveChanges(func, username) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(
        variables.courseId,
        variables.sectionId,
        variables.topicId,
        variables.oldPhrases,
        variables.newPhrases,
      );
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        courseQuerys.topicKeys.item(
          variables.courseId,
          variables.sectionId,
          variables.topicId,
          username,
        ),
        (old) => {
          if (!old) {
            return old;
          }
          const newPhrases = [...data];
          return {
            ...old,
            phrases: newPhrases,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemSections(
          variables.courseId,
          username,
        ),
      });
    },
  });
}

function ViewTopic(props) {
  const { t, i18n } = useTranslation();
  const [confirm] = useConfirm(t);
  const queryClient = useQueryClient();
  const {
    authData: { username, attributes },
    authenticated,
    handleSignIn,
    navigate,
    micPlaybackEnabled,
    correctSound,
    incorrectSound,
    verificationStatus,
    isCommunityAccessRestricted,
  } = props;

  const { addToQueue, toggleMuteSound, clearQueue, stop } = useAudioPlayer();

  const location = useLocation();
  const params = useParams();
  const { courseId, sectionId, topicId, classroomId } = params;
  const searchParams = searchStringToObj(location.search);
  const assignmentId = searchParams.assignmentId;

  const [prevTopic, setPrevTopic] = useState(null);
  const [nextTopic, setNextTopic] = useState(null);
  const [tts, translate] = useServices();
  const [timeoutArray, setTimeoutArray] = useState([]);

  const {
    openSnackbar,
    user,
    handleShareRoleplay,
    handleShareBot,
    handleOpenModerateDialog,
    reportProblem,
    startLoading,
    stopLoading,
    loading,
    markCommunityAccessRestricted,
  } = useApp();

  const {
    fetchCreatorSubscription,
    subscription
  } = useSubscription();

  const { setIsJoinCourseDialogOpen, setNewCourse, isCourseCreator } = useCourse();
  const { sendExerciseCompletionEmail } = notificationHelpers;

  const classroomAssignmentQuery = assignmentQuerys.useAssignment(
    async (classroomId, assignmentId) => {
      const assignment = await getAssignment(classroomId, assignmentId);
      return assignment.Item;
    },
    {
      classroomId,
      assignmentId,
    },
    { enabled: !!assignmentId },
  );

  const assignment =
    classroomAssignmentQuery.isSuccess && classroomAssignmentQuery.data
      ? classroomAssignmentQuery.data
      : [];

  const { uniqueAssignmentCoursesIds } = useMemo(() => {
    const courses = new Set();

    assignment?.exercises?.forEach(({ courseSectionTopicId }) => {
      if (courseSectionTopicId) {
        const [courseId] = courseSectionTopicId.split("|");
        courses.add(courseId);
      }
    });

    return {
      uniqueAssignmentCoursesIds: [...courses],
    };
  }, [assignment?.exercises]);

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { isMember, sectionsQuery } = useCourseState({
    courseId,
    username,
    getCourseSections,
    getCourseMember,
    getCourseMembers,
    memberId: username,
    visible: true,
  });

  const { learnLang, forLang, isCreator, collaborators } = course;

  const courseSections = useMemo(
    () =>
      sectionsQuery.isSuccess &&
        Array.isArray(sectionsQuery.data.Items) &&
        sectionsQuery.data.Items.length
        ? sectionsQuery.data.Items
        : [],
    [sectionsQuery.data, sectionsQuery.isSuccess],
  );

  const courseTopicList = useMemo(
    () =>
      (courseSections || []).flatMap((s) => {
        return s.topics.reduce(function (result, t) {
          if (isCreator || (isMember && !t.isTopicHidden)) {
            result.push(t);
          }
          return result;
        }, []);
      }),
    [courseSections, isCreator, isMember],
  );

  const topicQuery = courseQuerys.useTopic(
    async (cId, sId, tId) => {
      const topic = await getCourseSectionTopic(cId, sId, tId);
      const formattedTopic = await courseHelpers.formatTopic(
        topic.Item,
        props.authData,
      );
      return formattedTopic;
    },
    {
      courseId,
      sectionId,
      topicId,
      username: props.authData.username,
    },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const courseSectionTopic =
    topicQuery.isSuccess && topicQuery.data && topicQuery.data.topicId
      ? topicQuery.data
      : {};

  const topicFound =
    course &&
    course.courseId &&
    courseSectionTopic &&
    courseSectionTopic.topicId;

  const topicRoleplaysQuery = roleplayQuerys.useRoleplays(
    async (filters) => {
      const roleplays = await getRoleplays(filters);
      const formattedRoleplays = await roleplayHelpers.formatRoleplays(
        roleplays.Items,
        username,
      );
      return formattedRoleplays;
    },
    {
      filters: {
        courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
        limit: config.limits.roleplays,
      },
    },
    { enabled: !!username },
  );

  const topicRoleplays =
    topicRoleplaysQuery.isSuccess &&
      Array.isArray(topicRoleplaysQuery.data) &&
      topicRoleplaysQuery.data.length
      ? topicRoleplaysQuery.data
      : [];

  const topicBotsQuery = botQuerys.useBots(
    async (filters) => {
      const bots = await getBots(filters);
      const formattedBots = await botHelpers.formatBots(bots.Items, username);
      return formattedBots;
    },
    {
      filters: {
        courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
        limit: config.limits.bots,
      },
    },
    { enabled: !!username },
  );

  const topicBots =
    topicBotsQuery.isSuccess &&
      Array.isArray(topicBotsQuery.data) &&
      topicBotsQuery.data.length
      ? topicBotsQuery.data
      : [];

  const courseSectionTopicProgressQuery = progressQuerys.useTopicMemberProgress(
    async (courseId, sectionId, topicId, memberId, returnvalues) => {
      const memberProgress = await getCourseSectionTopicProgress(
        courseId,
        sectionId,
        topicId,
        memberId,
        returnvalues,
      );
      const formattedProgress = await progressHelpers.formatProgress(
        memberProgress.Items,
        username,
        attributes.preferred_username,
      );
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      topicId,
      memberId: username,
      returnvalues: null,
    },
    { enabled: !!username },
  );

  const topicProgress =
    courseSectionTopicProgressQuery.isSuccess &&
      Array.isArray(courseSectionTopicProgressQuery.data) &&
      courseSectionTopicProgressQuery.data.length
      ? courseSectionTopicProgressQuery.data
      : [];

  const courseSectionTopicCompletionsQuery =
    completionQuerys.useTopicMemberCompletions(
      async (courseId, sectionId, topicId, memberId, returnvalues) => {
        const memberCompletions = await getCourseSectionTopicCompletions(
          courseId,
          sectionId,
          topicId,
          memberId,
          returnvalues,
        );
        const formattedCompletions = await completionHelpers.formatCompletions(
          memberCompletions.Items,
          username,
          attributes.preferred_username,
        );
        return formattedCompletions;
      },
      {
        courseId,
        sectionId,
        topicId,
        memberId: username,
        returnvalues: null,
      },
      { enabled: !!username && !!courseId && !!sectionId && !!topicId },
    );

  const topicCompletions =
    courseSectionTopicCompletionsQuery.isSuccess &&
      Array.isArray(courseSectionTopicCompletionsQuery.data) &&
      courseSectionTopicCompletionsQuery.data.length
      ? courseSectionTopicCompletionsQuery.data
      : [];

  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username },
  );

  const parentClassroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const parentClassroom = useMemo(
    () =>
      parentClassroomQuery.isSuccess &&
        parentClassroomQuery.data &&
        parentClassroomQuery.data.classroomId
        ? parentClassroomQuery.data
        : {},
    [parentClassroomQuery.data, parentClassroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([parentClassroom]);
  }, [parentClassroom, markCommunityAccessRestricted]);

  const { courseSettings } = parentClassroom;

  const classroomMemberQuery = classroomQuerys.useClassroomMember(
    async () => {
      const member = await getClassroomMember(classroomId, username);
      return member;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroomMember =
    classroomMemberQuery.isSuccess &&
      classroomMemberQuery.data &&
      classroomMemberQuery.data.Item
      ? classroomMemberQuery.data.Item
      : {};

  useEffect(() => {
    if (courseTopicList && courseTopicList.length > 0) {
      const topicIndex = courseTopicList.findIndex(
        (t) => t.topicId === topicId,
      );
      if (topicIndex > -1) {
        if (courseTopicList[topicIndex - 1]) {
          setPrevTopic(courseTopicList[topicIndex - 1]);
        } else {
          setPrevTopic(null);
        }
        if (courseTopicList[topicIndex + 1]) {
          setNextTopic(courseTopicList[topicIndex + 1]);
        } else {
          setNextTopic(null);
        }
      }
    }
  }, [courseTopicList, topicId]);

  const creatorSubscriptionQuery = userQuerys.useSubscription(
    async (userId) => {
      const response = fetchCreatorSubscription(userId);
      return response;
    },
    {
      userId: course.createdBy,
    },
    {
      enabled: !!course.createdBy,
    },
  );

  const creatorSubscription = useMemo(
    () =>
      creatorSubscriptionQuery.isSuccess &&
        creatorSubscriptionQuery.data &&
        creatorSubscriptionQuery.data.plan
        ? creatorSubscriptionQuery.data
        : {},
    [creatorSubscriptionQuery.data, creatorSubscriptionQuery.isSuccess],
  );

  const classroomCreatorSubscriptionQuery = userQuerys.useSubscription(
    async (userId) => {
      const response = fetchCreatorSubscription(userId);
      return response;
    },
    {
      userId: parentClassroom.createdBy,
    },
    {
      enabled: !!parentClassroom.createdBy,
    },
  );

  const classroomCreatorSubscription = useMemo(
    () =>
      classroomCreatorSubscriptionQuery.isSuccess &&
        classroomCreatorSubscriptionQuery.data &&
        classroomCreatorSubscriptionQuery.data.plan
        ? classroomCreatorSubscriptionQuery.data
        : {},
    [
      classroomCreatorSubscriptionQuery.data,
      classroomCreatorSubscriptionQuery.isSuccess,
    ],
  );

  const avatarsQuery = avatarQuerys.useAvatars(async () => {
    const avatars = await getAvatars();
    return avatars;
  });

  const avatars =
    avatarsQuery.isSuccess &&
    Array.isArray(avatarsQuery.data) &&
    avatarsQuery.data.length
      ? avatarsQuery.data
      : [];

  // Mutations
  const updateCourseMutation = courseQuerys.useUpdateCourse(updateCourseAPI, {
    onMutate: () => {
      startLoading();
    },
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async () => {
      openSnackbar("course_updated", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: username,
          verbose: false,
        }),
      });
      queryClient.invalidateQueries(
        courseQuerys.courseKeys.item(courseId, username),
      );
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const updateCourseSectionTopicMutation =
    courseQuerys.useUpdateCourseSectionTopic(updateCourseSectionTopicAPI, {
      onSuccess: async () => {
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    });
  const deleteCourseSectionTopicMutation =
    courseQuerys.useDeleteCourseSectionTopic(deleteCourseSectionTopicAPI, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("deleted", "success");
        if (classroomId) {
          navigate(`/classrooms/${classroomId}/${courseId}`);
        } else {
          navigate(`/courses/${courseId}`);
        }
      },
      onError: () => {
        openSnackbar("problem_deleting", "error");
      },
    });
  const updateCourseSectionTopicPhrasesMutation =
    useUpdateCourseSectionTopicPhrases(
      updateCourseSectionTopicPhrases,
      username,
    );
  const updateCourseSectionTopicRoleplaysMutation =
    useUpdateCourseSectionTopicRoleplays(
      updateCourseSectionTopicRoleplays,
      username,
    );
  const updateCourseSectionTopicBotsMutation = useUpdateCourseSectionTopicBots(
    updateCourseSectionTopicBots,
    username,
  );
  const spreadsheetSaveChangesMutation = useSpreadsheetSaveChanges(
    spreadsheetSaveChanges,
    username,
  );
  const createCourseSectionTopicPhraseMutation =
    courseQuerys.useCreateCourseSectionTopicPhrase(
      createCourseSectionTopicPhraseAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("created", "success");
          queryClient.invalidateQueries({
            queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
          });
        },
        onError: () => {
          openSnackbar("problem_creating", "error");
        },
      },
    );
  const updateCourseSectionTopicPhraseMutation =
    courseQuerys.useUpdateCourseSectionTopicPhrase(
      updateCourseSectionTopicPhraseAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("Phrase updated", "success");
        },
        onError: () => {
          openSnackbar("problem", "error");
        },
      },
    );
  const deleteCourseSectionTopicPhraseMutation =
    courseQuerys.useDeleteCourseSectionTopicPhrase(
      deleteCourseSectionTopicPhraseAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("Phrase removed", "success");
          queryClient.invalidateQueries({
            queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
          });
        },
        onError: () => {
          openSnackbar("problem_deleting", "error");
        },
      },
    );
  const createCourseSectionTopicPhraseListMutation =
    courseQuerys.useCreateCourseSectionTopicPhraseList(
      createCourseSectionTopicPhraseListAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("created", "success");
        },
        onError: () => {
          openSnackbar("problem_creating", "error");
        },
      },
    );
  const updateCourseSectionTopicPhraseListMutation =
    courseQuerys.useUpdateCourseSectionTopicPhraseList(
      updateCourseSectionTopicPhraseListAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: (data, variables) => {
          openSnackbar("updated", "success");
          invalidateCache(DataType.TOPIC, {
            courseId: variables.courseId,
            sectionId: variables.sectionId,
            topicId: variables.topicId,
          });
        },
        onError: () => {
          openSnackbar("problem", "error");
        },
      },
    );
  const deleteCourseSectionTopicPhraseListMutation =
    courseQuerys.useDeleteCourseSectionTopicPhraseList(
      deleteCourseSectionTopicPhraseListAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("deleted", "success");
        },
        onError: () => {
          openSnackbar("problem_deleting", "error");
        },
      },
    );
  const duplicateCourseSectionTopicMutation =
    courseQuerys.useDuplicateCourseSectionTopic(courseHelpers.duplicateTopic, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: (data) => {
        if (data.topicId) {
          queryClient.setQueryData(
            courseQuerys.topicKeys.item(
              courseId,
              sectionId,
              data.topicId,
              username,
            ),
            data,
          );

          queryClient.setQueryData(
            roleplayQuerys.roleplayKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.topicId}`,
            }),
            data.roleplays,
          );

          queryClient.setQueryData(
            botQuerys.botKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.topicId}`,
            }),
            data.bots,
          );
          if (classroomId) {
            navigate(
              `/classrooms/${classroomId}/${courseId}/${sectionId}/${data.topicId}`,
            );
          } else {
            navigate(`/courses/${courseId}/${sectionId}/${data.topicId}`);
          }
        }
      },
      onError: () => { },
    });
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
      navigate("/courses");
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const updateBotMutation = botQuerys.useUpdateBot(updateBotAPI, {
    onSuccess: (data, variables) => {
      botHelpers.invalidateBotCache({
        botId: variables.botId,
        ...variables.bot,
        ...variables.values,
        ...data.Attributes,
      });
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
      });
      openSnackbar("updated", "success");
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const deleteBotMutation = botQuerys.useDeleteBot(deleteBotAPI, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("deleted", "success");
      await botHelpers.invalidateBotCache(variables.bot);
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const duplicateBotMutation = botQuerys.useDuplicateBot(
    botHelpers.duplicateBot,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data) => {
        await botHelpers.invalidateBotCache(data.Item);
        openSnackbar("created", "success");
        queryClient.invalidateQueries({
          queryKey: botQuerys.botKeys.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
        if (data && data.Item && data.Item.botId) {
          const { botId } = data.Item;
          if (courseId && sectionId && topicId) {
            if (classroomId) {
              navigate(
                `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
              );
            } else {
              navigate(
                `/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
              );
            }
          } else {
            navigate(`/bots/${botId}/edit`);
          }
        }
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const updateRoleplayMutation = roleplayQuerys.useUpdateRoleplay(
    updateRoleplayAPI,
    {
      onSuccess: (data, variables) => {
        roleplayHelpers.invalidateRoleplayCache({
          roleplayId: variables.roleplayId,
          ...variables.roleplay,
          ...variables.values,
          ...data.Attributes,
        });
        openSnackbar("updated", "success");
        queryClient.invalidateQueries({
          queryKey: roleplayQuerys.roleplayKeys.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const deleteRoleplayMutation = roleplayQuerys.useDeleteRoleplay(
    deleteRoleplayAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("deleted", "success");
        await roleplayHelpers.invalidateRoleplayCache(variables.roleplay);
        queryClient.invalidateQueries({
          queryKey: roleplayQuerys.roleplayKeys.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const duplicateRoleplayMutation = roleplayQuerys.useDuplicateRoleplay(
    roleplayHelpers.duplicateRoleplay,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data) => {
        await roleplayHelpers.invalidateRoleplayCache(data.Item);
        openSnackbar("created", "success");
        queryClient.invalidateQueries({
          queryKey: roleplayQuerys.roleplayKeys.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
        if (data && data.Item && data.Item.roleplayId) {
          const { roleplayId } = data.Item;
          if (courseId && sectionId && topicId) {
            if (classroomId) {
              navigate(
                `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
              );
            } else {
              navigate(
                `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
              );
            }
          } else {
            navigate(`/roleplays/${roleplayId}/edit`);
          }
        }
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const duplicatePhraseListMutation = courseQuerys.useDuplicatePhraseList(
    courseHelpers.duplicatePhraseList,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("created", "success");
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      },
    },
  );
  const createTopicProgressMutation =
    progressQuerys.useCreateTopicProgress(createProgressAPI);
  const deleteTopicProgressMutation = progressQuerys.useDeleteTopicProgress(
    deleteCourseSectionTopicProgressAPI,
    {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId: variables.courseId,
          sectionId: variables.sectionId,
          topicId: variables.topicId,
          exercise: variables.exercise,
          memberId: variables.memberId,
        });
        if (variables.courseId && variables.sectionId && variables.topicId) {
          queryClient.invalidateQueries({
            queryKey: progressQuerys.progressKeys.listTopicMember(
              variables.courseId,
              variables.sectionId,
              variables.topicId,
              variables.memberId,
              null,
            ),
          });
        }
      },
      onError: () => {
        // allow to fail without showing snackbar
        // openSnackbar('problem', 'error');
      },
    },
  );
  const createTopicCompletionMutation =
    completionQuerys.useCreateTopicCompletion(createCompletionAPI, {
      onSuccess: () => {
        sendCompletionEmail();
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    });

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await memoizedGetUser(userId);
      return response;
    },
    {
      userId: course.createdBy,
    },
    {
      enabled: !!course.createdBy,
    },
  );

  const courseCreator =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const { vchatEnabled, vchatEnabledInSettings } = parentClassroom;

  const isWaysideUser = username?.startsWith("Wayside");

  const isVideoChatEnabled =
    (isWaysideUser || isUserInternal) &&
    (vchatEnabled === true ||
      localStorage.getItem("vchat-enabled") === "true" ||
      (isCreator && vchatEnabled !== false) ||
      (isMember && vchatEnabled !== false && vchatEnabledInSettings));
  const isVideoChatEnabledInSettings =
    (isVideoChatEnabled && vchatEnabledInSettings === true) ||
    localStorage.getItem("vchat-enabled") === "true";

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        voiceSpeed,
        pitch,
      } = params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStartCourse = () => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId, username });
      }
    }
  };

  const handleLeaveCourse = () => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleViewCourse = () => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}`);
    } else {
      navigate(`/courses/${courseId}`);
    }
  };

  const handleDeleteCourseSectionTopic = () => {
    startLoading();
    deleteCourseSectionTopicMutation.mutate({
      courseId,
      sectionId,
      topicId,
      username,
    });
    const topicsWithVideo = course?.topicsWithVideo;
    if (topicsWithVideo && topicsWithVideo[topicId])
      delete topicsWithVideo[topicId];
    const topicsWithPdf = course?.topicsWithPdf;
    if (topicsWithPdf && topicsWithPdf[topicId]) delete topicsWithPdf[topicId];
    updateCourseMutation.mutate({
      courseId,
      values: { topicsWithVideo, topicsWithPdf },
      username,
    });
  };

  const isStudent =
    attributes?.["custom:role"] !== "teach" &&
    subscription &&
    subscription.isSubscriptionLoaded &&
    !subscription.isPaidUser &&
    !subscription.isUpgradePossible;

  const handleRemoveBot = (bot, saveLibrary) => {
    startLoading();
    deleteBotMutation.mutate({ botId: bot.botId, bot, saveLibrary });
  };

  const handleDuplicateTopic = (topic) => {
    startLoading();
    duplicateCourseSectionTopicMutation.mutate({
      values: window.structuredClone(topic),
      roleplays: window.structuredClone(topicRoleplays),
      bots: window.structuredClone(topicBots),
      context: "view-topic",
      username,
    });
  };

  const handleUpdateRoleplay = async (roleplay, values) => {
    try {
      if (values.roleplayId) {
        delete values.roleplayId;
      }
      if (values.actor1 && values.actor1.picture) {
        values.actor1PictureWarnDetails = {};
        const base64String = values.actor1.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplay.roleplayId}-actor1`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.actor1PictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.actor1.picture = picture;
          values.actor1.pictureS3Key = pictureS3Key;
        } else {
          delete values.actor1.picture;
        }
      }
      if (values.actor2 && values.actor2.picture) {
        values.actor2PictureWarnDetails = {};
        const base64String = values.actor2.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplay.roleplayId}-actor2`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.actor2PictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.actor2.picture = picture;
          values.actor2.pictureS3Key = pictureS3Key;
        } else {
          delete values.actor2.picture;
        }
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplay.roleplayId}`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      updateRoleplayMutation.mutate({
        roleplayId: roleplay.roleplayId,
        values,
        roleplay,
      });
      return true;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleUpdateBot = async (bot, values) => {
    try {
      if (values.botId) {
        delete values.botId;
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadBotImage(base64String[1] || base64String[0], bot.botId);
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.botAvatar) {
        values.botAvatarWarnDetails = {};
        const base64String = values.botAvatar.split(",");
        const [
          botAvatar,
          botAvatarS3Key,
          botModerated,
          botWarn,
          botModeratedLabels,
        ] = await UploadBotImage(
          base64String[1] || base64String[0],
          `${bot.botId}-botAvatar`,
        );
        if (botModerated || botWarn) {
          if (verificationStatus === "approved" || botWarn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${botModeratedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.botAvatarWarnDetails = {
              user: username,
              labels: botModeratedLabels.join(", "),
            };
          } else {
            const moderationReason = botModeratedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (botAvatar) {
          values.botAvatar = botAvatar;
          values.botAvatarS3Key = botAvatarS3Key;
        } else {
          delete values.botAvatar;
        }
      }
      updateBotMutation.mutate({ botId: bot.botId, values, bot });
    } catch (error) {
      logger.error(error);
    }
  };

  const handleUpdatePhraseGroup = async (phraseGroup, values) => {
    try {
      const phraseGroups = courseSectionTopic.phraseGroups || [];

      // Strip phrases before saving — they're joined at formatTopic time, not stored in DB
      const phraseGroupsForSave = phraseGroups.map((pg) => {
        const { phrases, ...pgWithoutPhrases } = pg;
        if (pg.phraseGroupId === phraseGroup.phraseGroupId) {
          return { ...pgWithoutPhrases, ...values };
        }
        return pgWithoutPhrases;
      });

      // Call API directly — avoids courseQuerys.useUpdateCourseSectionTopic's
      // onSuccess handler which would overwrite the cache with phrases stripped out
      await updateCourseSectionTopicAPI(courseId, sectionId, topicId, {
        phraseGroups: phraseGroupsForSave,
      });

      // Surgical cache patch — only updates the changed phraseGroup,
      // keeps phrases and all other fields on every phraseGroup intact
      queryClient.setQueryData(
        courseQuerys.topicKeys.item(courseId, sectionId, topicId, username),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            phraseGroups: (old.phraseGroups || []).map((pg) =>
              pg.phraseGroupId === phraseGroup.phraseGroupId
                ? { ...pg, ...values }
                : pg
            ),
          };
        }
      );
    } catch (error) {
      logger.error(error);
      openSnackbar("problem", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // These three stay the same — they read state from the phraseGroup
  // arg which comes from the live cache via activePhraseGroup in Topic.jsx
  // ─────────────────────────────────────────────────────────────

  const handleTogglePhraseGroupGameVisibility = async (phraseGroup, gameId) => {
    try {
      const hiddenPhraseGroupGames = phraseGroup.hiddenPhraseGroupGames || {};
      await handleUpdatePhraseGroup(phraseGroup, {
        hiddenPhraseGroupGames: {
          ...hiddenPhraseGroupGames,
          [gameId]: !hiddenPhraseGroupGames[gameId],
        },
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const handleTogglePhraseGroupKeyboard = async (phraseGroup, gameId) => {
    try {
      const isKeyboardHiddenPhraseGroupGames =
        phraseGroup.isKeyboardHiddenPhraseGroupGames || {};
      await handleUpdatePhraseGroup(phraseGroup, {
        isKeyboardHiddenPhraseGroupGames: {
          ...isKeyboardHiddenPhraseGroupGames,
          [gameId]: !isKeyboardHiddenPhraseGroupGames[gameId],
        },
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const handleTogglePhraseGroupWordBank = async (phraseGroup, gameId) => {
    try {
      const hiddenWordBankPhraseGroupGames =
        phraseGroup.hiddenWordBankPhraseGroupGames || {};
      await handleUpdatePhraseGroup(phraseGroup, {
        hiddenWordBankPhraseGroupGames: {
          ...hiddenWordBankPhraseGroupGames,
          [gameId]: !hiddenWordBankPhraseGroupGames[gameId],
        },
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const handleUpdateTopic = async (values, { setSubmitting, resetForm }) => {
    try {
      if (
        values.visibility &&
        values.visibility !== "public" &&
        values.visibility !== "private"
      ) {
        throw new Error();
      }
      if (values.picture && values.picture !== courseSectionTopic.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadTopicImage(
            base64String[1] || base64String[0],
            `${topicId}-picture`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.video && values.video !== course.video) {
        startLoading();
        const { base: videoBase, ext: videoExt } = splitFileName(values.video.video.name);
        const videoPath = `video/course/${courseId}/section/${sectionId}/topic/${topicId}/${videoBase}-${Date.now()}${videoExt}`;
        const response = await getSignedUploadUrl({
          fileName: videoPath,
          fileType: values.video.video.type,
        });
        const { url } = response.body;
        await uploadVideo(url, values.video.video);
        const topicsWithVideo = course?.topicVideo || {};
        if (topicsWithVideo) topicsWithVideo[topicId] = true;
        updateCourseMutation.mutate({
          courseId,
          values: { topicsWithVideo },
          username,
        });
        delete values.video;
        values.videoUrl = `${AssetsURL}/${videoPath}`;
        stopLoading();
      }
      if (values.banner && values.banner !== courseSectionTopic.banner) {
        values.bannerWarnDetails = {};
        const base64String = values.banner.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadTopicImage(
            base64String[1] || base64String[0],
            `${topicId}-banner`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.bannerWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.banner = picture;
          values.bannerS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.pdf && values.pdf !== courseSectionTopic.pdf) {
        startLoading();
        const [
          url,
          pdfKey,
          vectorStoreFileName,
          hashKey,
          pdfVectorStoreCreationStatus,
        ] = await uploadPDF(values.pdf, "pdf", courseSectionTopic.topicId);
        const topicsWithPdf = course?.topicsWithPdf || {};
        topicsWithPdf[topicId] = true;
        updateCourseMutation.mutate({
          courseId,
          values: { topicsWithPdf },
          username,
        });
        if (url) {
          values.pdf = url;
          values.pdfS3Key = pdfKey;
          values.pdfVectorStoreCreationStatus = pdfVectorStoreCreationStatus;
        } else {
          delete values.pdf;
        }
        if (vectorStoreFileName && hashKey && pdfVectorStoreCreationStatus) {
          values.pdfDetails = {
            fileName: vectorStoreFileName,
            key: pdfKey,
            hashKey,
          };
        }
        stopLoading();
      }
      updateCourseSectionTopicMutation.mutate({
        courseId,
        sectionId,
        topicId,
        values,
        username,
      });
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    } catch (error) {
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    }
  };

  const handleDeleteTopicVideo = async (sectionId, topicId, s3Path) => {
    try {
      // console.log({ sectionId, topicId, s3Path})
      startLoading();
      const values = {
        videoUrl: "",
      };
      if (s3Path) {
        const parts = s3Path.split("nualang.com");
        let videoPathOnly = parts[1].startsWith("/")
          ? parts[1].substring(1)
          : parts[1];
        videoPathOnly = utf8ToB64(videoPathOnly); // base64 encode the video path
        await deleteVideo(videoPathOnly);
      }
      updateCourseSectionTopicMutation.mutate(
        { courseId, sectionId, topicId, values, username },
        {
          onSuccess: async () => {
            const topicsWithVideo = course?.topicsWithVideo;
            if (topicsWithVideo && topicsWithVideo[topicId])
              delete topicsWithVideo[topicId];
            updateCourseMutation.mutate({
              courseId,
              values: { topicsWithVideo },
              username,
            });
            openSnackbar("topic_updated", "success");
            stopLoading();
          },
          onError: () => {
            openSnackbar("problem", "error");
            stopLoading();
          },
        },
      );
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const validatePhraseRecognition = async (phrase) => {
    try {
      const tag = config.languages[`${courseSectionTopic.learnLang}`].Tag;
      const voice = config.languages[`${courseSectionTopic.learnLang}`].Voice;
      const result = await tts(
        phrase,
        courseSectionTopic.learnLang,
        voice,
        undefined,
        undefined,
        undefined,
        true,
      );
      const speechContextsWithBoost = [
        {
          phrases: [
            ...(phrase.length < 100
              ? [removeExtraWhiteSpaces(phrase)]
              : removeExtraWhiteSpaces(phrase).split(" ")),
          ],
          boost: 10,
        },
      ];
      const recognitionWithBoost = await validateSpeechRecognition({
        audioUrl: result.audioUrl,
        languageCode: tag,
        speechContexts: speechContextsWithBoost,
      });
      const alternativeTranscript = (recognitionWithBoost.results || []).map(
        (result) => {
          const alternatives = result.alternatives;
          return alternatives;
        },
      );
      return alternativeTranscript;
    } catch (err) {
      logger.error(err);
    }
  };

  const handleCreatePhrase = async (courseId, sectionId, topicId, values) => {
    if (learnLang === forLang) {
      if (!values.phrase) {
        openSnackbar("missing_phrase_details", "error");
        return;
      }
    } else if (learnLang !== forLang) {
      if (
        !values.phrase ||
        !(Array.isArray(values.translations) && values.translations.length > 0)
      ) {
        openSnackbar("missing_phrase_details", "error");
        return;
      }
    }
    if (values.image) {
      values.imageWarnDetails = {};
      const base64String = values.image.split(",");
      const [image, imageS3Key, moderated, warn, moderatedLabels] =
        await UploadPhraseImage(base64String[1] || base64String[0], uuidv4());
      if (moderated || warn) {
        if (verificationStatus === "approved" || warn) {
          const confirmation = await confirm(
            `${t("warning")}`,
            `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
            `${t("use_anyways")}`,
          );
          if (confirmation !== true) {
            return;
          }
          values.imageWarnDetails = {
            user: username,
            labels: moderatedLabels.join(", "),
          };
        } else {
          const moderationReason = moderatedLabels[0];
          handleOpenModerateDialog(moderationReason);
          return;
        }
      }
      if (image) {
        values.image = image;
        values.imageS3Key = imageS3Key;
      } else {
        delete values.image;
      }
    }
    startLoading();
    const response = await createCourseSectionTopicPhraseMutation.mutateAsync({
      courseId,
      sectionId,
      topicId,
      values: {
        ...values,
        idx: Date.now(),
      },
      username,
    });

    if (response.Item) {
      return response.Item;
    }
  };

  const handleUpdatePhrase = async (
    courseId,
    sectionId,
    topicId,
    phraseId,
    values,
  ) => {
    const isUpdatingVoices =
      values && values.voices && values.voices.length > 0;
    if (!isUpdatingVoices) {
      if (learnLang === forLang) {
        if (!values.phrase) {
          openSnackbar("missing_phrase_details", "error");
          return;
        }
      } else if (learnLang !== forLang) {
        if (
          !values.phrase ||
          !(
            Array.isArray(values.translations) && values.translations.length > 0
          )
        ) {
          openSnackbar("missing_phrase_details", "error");
          return;
        }
      }
    }

    if (values.image) {
      values.imageWarnDetails = {};
      const base64String = values.image.split(",");
      const [image, imageS3Key, moderated, warn, moderatedLabels] =
        await UploadPhraseImage(base64String[1] || base64String[0], uuidv4());
      if (moderated || warn) {
        if (verificationStatus === "approved" || warn) {
          const confirmation = await confirm(
            `${t("warning")}`,
            `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
            `${t("use_anyways")}`,
          );
          if (confirmation !== true) {
            return;
          }
          values.imageWarnDetails = {
            user: username,
            labels: moderatedLabels.join(", "),
          };
        } else {
          const moderationReason = moderatedLabels[0];
          handleOpenModerateDialog(moderationReason);
          return;
        }
      }
      if (image) {
        values.image = image;
        values.imageS3Key = imageS3Key;
      } else {
        delete values.image;
      }
    }

    try {
      startLoading();
      const response = await updateCourseSectionTopicPhraseMutation.mutateAsync(
        {
          courseId,
          sectionId,
          topicId,
          phraseId,
          values,
          username,
        },
      );
      return response;
    } catch (error) {
      logger.error(error);
    }
  };

  const handleUpdatePhrases = async (courseId, sectionId, topicId, phrases) => {
    try {
      startLoading();
      const response =
        await updateCourseSectionTopicPhrasesMutation.mutateAsync(
          {
            courseId,
            sectionId,
            topicId,
            phrases,
          },
          {
            onSettled: () => {
              // Error or success... doesn't matter!
              stopLoading();
            },
            onSuccess: () => {
              invalidateCache(DataType.PHRASE_ARRAY, {
                courseId,
                sectionId,
                topicId,
              });
              openSnackbar("updated", "success");
            },
            onError: () => {
              openSnackbar("problem", "error");
            },
          },
        );
      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleUpdateRoleplays = async (
    courseId,
    sectionId,
    topicId,
    roleplays,
  ) => {
    try {
      startLoading();
      const response =
        await updateCourseSectionTopicRoleplaysMutation.mutateAsync({
          courseId,
          sectionId,
          topicId,
          roleplays,
        });
      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleUpdateBots = async (courseId, sectionId, topicId, bots) => {
    try {
      startLoading();
      const response = await updateCourseSectionTopicBotsMutation.mutateAsync(
        {
          courseId,
          sectionId,
          topicId,
          bots,
        },
        {
          onSettled: () => {
            // Error or success... doesn't matter!
            stopLoading();
          },
          onSuccess: () => {
            invalidateCache(DataType.BOT_ARRAY, {
              courseId,
              sectionId,
              topicId,
            });
            openSnackbar("updated", "success");
          },
          onError: () => {
            openSnackbar("problem", "error");
          },
        },
      );
      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleSpreadsheetSaveChanges = async (
    oldPhrases = [],
    newPhrases = [],
  ) => {
    try {
      startLoading();
      const response = await spreadsheetSaveChangesMutation.mutateAsync(
        {
          courseId,
          sectionId,
          topicId,
          oldPhrases,
          newPhrases,
        },
        {
          onSettled: () => {
            // Error or success... doesn't matter!
            stopLoading();
          },
          onSuccess: () => {
            invalidateCache(DataType.PHRASE_ARRAY, {
              courseId,
              sectionId,
              topicId,
            });
            openSnackbar("Phrases uploaded", "success");
          },
          onError: () => {
            openSnackbar("problem_creating", "error");
          },
        },
      );
      stopLoading();
      return response;
    } catch (error) {
      stopLoading();
      logger.error(error);
      return false;
    }
  };

  const handleDeletePhrase = async (courseId, sectionId, topicId, phraseId) => {
    startLoading();
    await deleteCourseSectionTopicPhraseMutation.mutateAsync({
      courseId,
      sectionId,
      topicId,
      phraseId,
      username,
    });
  };

  const handleCreatePhraseList = async (values) => {
    try {
      startLoading();
      const voicesForLang = learnLang
        ? config.languages[learnLang.toLowerCase() || "english"].Voices
        : null;
      const allVoices = Object.entries(voicesForLang || {}).flatMap(([, dialect]) => {
        if (!Array.isArray(dialect?.voiceOptions)) return [];
        return dialect.voiceOptions
          .filter((v) => v?.name  && !v?.legacy)
          .map((v) => ({ name: v.name, languageCode: dialect.languageCode }));
      });
      const randomVoice = allVoices.length > 0
        ? allVoices[Math.floor(Math.random() * allVoices.length)]
        : null;
      const response =
        await createCourseSectionTopicPhraseListMutation.mutateAsync({
          courseId,
          sectionId,
          topicId,
          values: {
            ...(courseSectionTopic.picture && { picture: courseSectionTopic.picture }),
            ...(courseSectionTopic.pictureS3Key && { pictureS3Key: courseSectionTopic.pictureS3Key }),
            ...(randomVoice && { voice: randomVoice.name, voiceLanguageCode: randomVoice.languageCode }),
            ...values,
          },
          username,
        }); 
      if (response.Item) {
        return response.Item;
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handlePlayPhraseList = (phraseList) => {
    const { courseSectionTopicId, phraseListId } = phraseList;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const cId = idValues[0].trim();
      const sId = idValues[1].trim();
      const tId = idValues[2].trim();
      if (classroomId) {
        navigate(`/classrooms/${classroomId}/${cId}/${sId}/${tId}/phrase-lists/${phraseListId}`);
      } else {
        navigate(`/courses/${cId}/${sId}/${tId}/phrase-lists/${phraseListId}`);
      }
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}/phrase-lists/${phraseListId}`);
    }
  };

  const handleEditPhraseList = (phraseList) => {
    const { courseSectionTopicId, phraseListId } = phraseList;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const cId = idValues[0].trim();
      const sId = idValues[1].trim();
      const tId = idValues[2].trim();
      if (classroomId) {
        navigate(`/classrooms/${classroomId}/${cId}/${sId}/${tId}/phrase-lists/${phraseListId}/edit`);
      } else {
        navigate(`/courses/${cId}/${sId}/${tId}/phrase-lists/${phraseListId}/edit`);
      }
    } else {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}/phrase-lists/${phraseListId}/edit`);
    }
  };

  const handleUpdatePhraseList = async (phraseListId, values) => {
    try {
      startLoading();
      if (values.picture) {
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadPhraseListImage(
            base64String[1] || base64String[0],
            phraseListId,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              stopLoading();
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            stopLoading();
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      const response =
        await updateCourseSectionTopicPhraseListMutation.mutateAsync({
          courseId,
          sectionId,
          topicId,
          phraseListId,
          values,
          username,
        });
      return response;
    } catch (error) {
      logger.error(error);
    }
  };

  const handleDeletePhraseList = async (phraseListId) => {
    try {
      startLoading();
      await deleteCourseSectionTopicPhraseListMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        phraseListId,
        username,
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const viewNextTopic = () => {
    if (nextTopic === null) {
      if (classroomId) {
        navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}`);
      } else {
        navigate(`/courses/${courseId}/${sectionId}`);
      }
    } else {
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${nextTopic.courseSectionId.split("|")[1]
          }/${nextTopic.topicId}`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${nextTopic.courseSectionId.split("|")[1]}/${nextTopic.topicId
          }`,
        );
      }
    }
  };

  const viewPrevTopic = () => {
    if (prevTopic === null) {
      if (classroomId) {
        navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}`);
      } else {
        navigate(`/courses/${courseId}/${sectionId}`);
      }
    } else {
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${prevTopic.courseSectionId.split("|")[1]
          }/${prevTopic.topicId}`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${prevTopic.courseSectionId.split("|")[1]}/${prevTopic.topicId
          }`,
        );
      }
    }
  };

  const handleToggleExerciseVisibility = async (exercise, action) => {
    try {
      const hiddenExercises = courseSectionTopic.hiddenExercises || [];

      let newExercises;

      if (action === "hide") {
        newExercises = [...hiddenExercises, exercise];
      } else {
        newExercises = hiddenExercises.filter((e) => e !== exercise);
      }

      const response = await handleUpdateTopic(
        {
          hiddenExercises: newExercises,
          topicName: courseSectionTopic.topicName,
        },
        {},
      );

      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleToggleExerciseWordBank = async (exercise, action) => {
    try {
      const hiddenWordBankExercises =
        courseSectionTopic.hiddenWordBankExercises || [];
      let newExercises;

      if (action === "hide") {
        newExercises = [...hiddenWordBankExercises, exercise];
      } else {
        newExercises = hiddenWordBankExercises.filter((e) => e !== exercise);
      }

      const response = await handleUpdateTopic(
        {
          hiddenWordBankExercises: newExercises,
        },
        {},
      );

      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleToggleKeyboardTranslation = async (action) => {
    try {
      const response = await handleUpdateTopic(
        {
          isKeyboardHiddenTranslation: action,
        },
        {},
      );

      return response;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handlePlayBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}`,
        );
      } else {
        navigate(`/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}`);
      }
    } else {
      navigate(`/bots/${botId}`);
    }
  };

  const handlePlayRoleplay = async (roleplay) => {
    const { courseSectionTopicId, roleplayId } = roleplay;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}`,
        );
      }
    } else {
      navigate(`/roleplays/${roleplayId}`);
    }
  };

  const handleEditBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      }
    } else {
      navigate(`/bots/${botId}/edit`);
    }
  };

  const handleEditRoleplay = async (roleplay) => {
    const { courseSectionTopicId, roleplayId } = roleplay;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
        );
      }
    } else {
      navigate(`/roleplays/${roleplayId}/edit`);
    }
  };

  const handleUploadAudio = async (name, src) =>
    UploadPhraseAudio(src, `${courseId}|${sectionId}|${topicId}-${name}`);

  const handleDuplicateBot = async (values) => {
    startLoading();
    duplicateBotMutation.mutate({ values, courseId, sectionId, topicId });
  };

  const handleDuplicatePhraseList = async (phraseList) => {
    startLoading();
    const response =
      await duplicatePhraseListMutation.mutateAsync({
        courseId,
        sectionId,
        topicId,
        values: phraseList,
        username,
      });
    if (response.Item) {
      return response.Item;
    }
  };

  const handleDuplicateRoleplay = async (values) => {
    startLoading();
    duplicateRoleplayMutation.mutate({ values, courseId, sectionId, topicId });
  };

  const handleUploadBotExerciseImage = async (botId, value) => {
    try {
      if (botId && value) {
        const imageId = uuidv4();
        const pictureBase64String = value.split(",");
        const [url] = await UploadBotExerciseImage(
          botId,
          pictureBase64String[1] || pictureBase64String[0],
          imageId,
        );
        return url;
      }
      throw new Error(t("missing_details"));
    } catch (error) {
      logger.error(error);
      openSnackbar(error.message, "error");
      return null;
    }
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const handleReportProblem = async (
    problem,
    shareAudio,
    audioBlob,
    shareScreenshot,
    screenshot,
  ) => {
    try {
      const { type, description, currentPhrase } = problem;
      const isCreatorReceivingReportedProblem = [true, undefined].includes(
        courseCreator?.UserAttributes?.email_notification
          ?.student_reports_problem,
      );
      const isTeacher = user
        ? user.attributes["custom:role"] === "teach"
        : false;

      let recipients;
      if (type === "equipment_problem") {
        const teacherUsername = parentClassroom && parentClassroom.createdBy;
        recipients = [teacherUsername];
      } else {
        recipients =
          (isCreatorReceivingReportedProblem || isTeacher) &&
            type === "content_problem"
            ? [courseCreator.Username, ...collaborators, config.supportEmail]
            : [config.supportEmail];
      }

      const problemType = (() => {
        switch (type) {
          case "equipment_problem":
            return "Equipment Problem";
          case "technical_problem":
            return "Technical Problem";
          case "content_problem":
            return "Content Problem";
          default:
            return "Other Problem";
        }
      })();

      const [audioUrl, screenshotUrl] = await Promise.all([
        shareAudio && audioBlob
          ? UploadReportProblemAudio(audioBlob, username)
          : Promise.resolve(null),
        shareScreenshot && screenshot
          ? UploadReportProblemImage(screenshot, username)
          : Promise.resolve(null),
      ]);

      const body = {
        recipients,
        params: {
          exerciseLink: `${window.location.origin}${window.location.pathname}`,
          exerciseName: `${courseSectionTopic.topicName} - ${searchParams.exercise}`,
          studentName: user ? user.attributes.preferred_username : "",
          problemDescription: description,
          problemType: problemType,
          email: user ? user.attributes.email : "",
          username: user ? user.attributes.preferred_username : "",
          role: user ? user.attributes["custom:role"] : "",
          browser: JSON.stringify(
            parseUserAgent(window.navigator.userAgent).browser,
          ),
          os: JSON.stringify(parseUserAgent(window.navigator.userAgent).os),
          isWaysideUser: user?.username?.startsWith("Wayside"),
          phrase: currentPhrase?.phrase,
          ...(audioUrl && { audioUrl }),
          ...(screenshotUrl && { screenshotUrl }),
        },
      };
      await reportProblem(body);
    } catch (error) {
      logger.error(error);
    }
  };

  const sendCompletionEmail = async () => {
    try {
      if (course.createdBy) {
        if (
          courseCreator.UserAttributes &&
          courseCreator.UserAttributes.email_notification &&
          courseCreator.UserAttributes.email_notification
            .student_completes_exercise === true &&
          user.attributes.username !== course.createdBy
        ) {
          const studentName = user ? user.attributes.preferred_username : "";
          const url = `${window.location.origin}/courses/${courseId}/${sectionId}/${topicId}/activity/${searchParams.exercise}`;
          sendExerciseCompletionEmail(
            [courseCreator.UserAttributes.email],
            studentName,
            courseCreator.UserAttributes.preferred_username,
            courseSectionTopic.topicName,
            searchParams.exercise,
            url,
          );
        }
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handleNotesImageUpload = (file, onSuccess, onError) => {
    try {
      if (file) {
        if (file.size < config.fileSizeLimit) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener(
            "load",
            async () => {
              const imageId = uuidv4();
              const imageBase64String = reader.result.split(",");
              const [picture, , moderated, warn, moderatedLabels] =
                await UploadTopicImage(
                  imageBase64String[1] || imageBase64String[0],
                  `${topicId}-notes-${imageId}`,
                );
              if (moderated || warn) {
                if (verificationStatus === "approved" || warn) {
                  const confirmation = await confirm(
                    `${t("warning")}`,
                    `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                    `${t("use_anyways")}`,
                  );
                  if (confirmation !== true) {
                    return;
                  } else if (confirmation === true) {
                    onSuccess(picture);
                  }
                } else {
                  const moderationReason = moderatedLabels[0];
                  handleOpenModerateDialog(moderationReason);
                  return;
                }
              } else {
                onSuccess(picture);
              }
            },
            false,
          );
        } else {
          onError("File size exceeded");
        }
      }
    } catch (error) {
      logger.error(error);
      onError(error.message);
    }
  };

  const [isSelectClassroomDialogOpen, setIsSelectClassroomDialogOpen] =
    useState(false);

  const handleOpenSelectClassroomDialog = () => {
    setIsSelectClassroomDialogOpen(true);
  };

  const handleCloseSelectClassroomDialog = () => {
    setIsSelectClassroomDialogOpen(false);
  };

  const handleSaveProgress = (values) => {
    createTopicProgressMutation.mutate(
      { values },
      {
        onSuccess: (data, variables) => {
          progressHelpers.invalidateProgressCache({
            courseId: variables.values.courseId,
            sectionId: variables.values.sectionId,
            topicId: variables.values.topicId,
            exercise: variables.values.exercise,
            memberId: username,
          });
        },
        onError: () => {
          openSnackbar("problem", "error");
        },
      },
    );
  };

  const uploadAudioAttempt = async (audioBlob) => {
    const url = await UploadExerciseAudioAttempt(
      audioBlob,
      `${courseId}|${sectionId}|${topicId}_${uuidv4()}`,
    );
    return url;
  };

  const handleCreateCompletion = (values) => {
    const classroomCourseIds =
      parentClassroom?.courses?.length > 0
        ? parentClassroom.courses.toString()
        : null;
    const classroomAssignedCourseIds =
      parentClassroom?.assignedCourses?.length > 0
        ? parentClassroom.assignedCourses.toString()
        : null;
    createTopicCompletionMutation.mutate({
      values,
      classroomCourseIds,
      uniqueAssignmentCoursesIds,
      classroomAssignedCourseIds,
    });
  };

  const handleDeleteProgress = async (
    courseId,
    sectionId,
    topicId,
    exercise,
    { roleplayId, botId, phraseListId } = {},
  ) => {
    deleteTopicProgressMutation.mutate({
      courseId,
      sectionId,
      topicId,
      exercise,
      memberId: username,
      roleplayId,
      botId,
      phraseListId,
    });
  };

  const handleRemoveRoleplay = (roleplay, saveLibrary) => {
    startLoading();
    deleteRoleplayMutation.mutate({
      roleplayId: roleplay.roleplayId,
      roleplay,
      saveLibrary,
    });
  };

  const teachClassrooms =
    teachClassroomsQuery.isSuccess &&
      Array.isArray(teachClassroomsQuery.data) &&
      teachClassroomsQuery.data.length
      ? teachClassroomsQuery.data
      : [];

  const learnLangCharacters = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].characters
    : [];
  const forLangCharacters = getForLangCharacters(forLang);
  let specialCharacters = learnLangCharacters.concat(forLangCharacters);
  specialCharacters = [...new Set(specialCharacters)];

  const isTopicProgressLoading = courseSectionTopicProgressQuery.isLoading;

  const handleDeletePDF = async () => {
    try {
      startLoading();
      const values = {
        pdf: "",
        pdfS3Key: "",
        pdfDetails: {},
        pdfVectorStoreCreationStatus: "",
      };
      if (
        courseSectionTopic &&
        courseSectionTopic.pdfDetails &&
        courseSectionTopic.pdfVectorStoreCreationStatus ===
        "CREATION_COMPLETE" &&
        stage === "dev"
      ) {
        const { fileName, hashKey } = courseSectionTopic.pdfDetails;
        const body = {
          itemsToDelete: [
            {
              fileName,
              hashKey,
            },
          ],
          type: "file",
        };
        await deleteVectorStore(body);
      }
      updateCourseSectionTopicMutation.mutate(
        { courseId, sectionId, topicId, values, username },
        {
          onSuccess: async () => {
            const topicsWithPdf = course?.topicsWithPdf;
            if (topicsWithPdf && topicsWithPdf[topicId])
              delete topicsWithPdf[topicId];
            updateCourseMutation.mutate({
              courseId,
              values: { topicsWithPdf },
              username,
            });
            stopLoading();
            openSnackbar("updated", "success");
          },
          onError: () => {
            openSnackbar("problem", "error");
            stopLoading();
          },
        },
      );
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const topicGoal = courseSectionTopic.topicGoal ?? "";
  const isNualangLiveEnabled =
    parentClassroom.classroomId &&
    classroomCreatorSubscription?.isPaidUser &&
    parentClassroom.isCreator;

  const { createdGame } = useClassroomLiveGame({
    classroomId,
    isClassroomCreator: parentClassroom?.isCreator,
    username,
    authData: props.authData,
    context: "view-classroom",
    isNualangLiveEnabled,
  });

  const isTopicLoading =
    topicQuery.isLoading ||
    courseQuery.isLoading ||
    isTopicProgressLoading ||
    parentClassroomQuery.isLoading ||
    topicRoleplaysQuery.isLoading ||
    topicBotsQuery.isLoading;

  const topicWithCompletions = topicFound
    ? {
      ...courseSectionTopic,
      completions: topicCompletions,
      picture: courseSectionTopic.picture || "",
    }
    : null;

  // ?exercise=bot-<id> / roleplay-<id> — set when arriving from a search result
  // card so the exercise list scrolls to that item.
  const focusExerciseId =
    new URLSearchParams(location.search).get("exercise") || undefined;

  return (
    <>
      {isTopicLoading ? (
        <ViewTopicScreen
          t={t}
          isLoading={isTopicLoading}
          placeholderImageUrl={TopicImage}
          handleViewCourse={handleViewCourse}
          topic={{}}
          courseSettings={courseSettings?.[courseId]}
        />
      ) : (
        <ViewTopicScreen
          t={t}
          isLoading={isTopicLoading}
          location={location}
          navigate={navigate}
          focusExerciseId={focusExerciseId}
          handleDuplicateBot={handleDuplicateBot}
          handleDuplicateRoleplay={handleDuplicateRoleplay}
          handleUpdateRoleplays={handleUpdateRoleplays}
          handleUpdateBots={handleUpdateBots}
          nextTopic={nextTopic}
          prevTopic={prevTopic}
          placeholderImageUrl={TopicImage}
          handleViewCourse={handleViewCourse}
          viewNextTopic={viewNextTopic}
          viewPrevTopic={viewPrevTopic}
          courseId={courseId}
          sectionId={sectionId}
          topicId={topicId}
          topic={topicWithCompletions}
          roleplays={topicRoleplays}
          bots={topicBots}
          learnLang={learnLang}
          forLang={forLang}
          isCreator={isCreator}
          isMember={isMember}
          isReadWriteModeStudent={
            classroomMember?.assignedLabels?.isReadWriteModeStudent
          }
          userAttributes={user.attributes}
          handleStartCourse={handleStartCourse}
          handleLeaveCourse={handleLeaveCourse}
          handleDeleteTopic={handleDeleteCourseSectionTopic}
          handleDuplicateTopic={handleDuplicateTopic}
          handleTranslate={translate}
          handleSpeak={handleSpeak}
          clearQueue={clearQueue}
          stopAudio={stop}
          handleUpdateTopic={handleUpdateTopic}
          handleToggleExerciseWordBank={handleToggleExerciseWordBank}
          handleToggleExerciseVisibility={handleToggleExerciseVisibility}
          handleCreatePhrase={handleCreatePhrase}
          handleUpdatePhrase={handleUpdatePhrase}
          handleUpdatePhrases={handleUpdatePhrases}
          uploadAudio={handleUploadAudio}
          handleDeletePhrase={handleDeletePhrase}
          micPlaybackEnabled={micPlaybackEnabled}
          openSnackbar={openSnackbar}
          topicProgress={topicProgress}
          handleDeleteProgress={handleDeleteProgress}
          handleSaveProgress={handleSaveProgress}
          handleComplete={handleCreateCompletion}
          handlePlayRoleplay={handlePlayRoleplay}
          handleUpdateRoleplay={handleUpdateRoleplay}
          handleRemoveRoleplay={handleRemoveRoleplay}
          handleUpdateBot={handleUpdateBot}
          handleRemoveBot={handleRemoveBot}
          handleMessageBot={messageBot}
          isCourseCreator={isCourseCreator}
          handlePlayBot={handlePlayBot}
          handleEditBot={handleEditBot}
          handleEditRoleplay={handleEditRoleplay}
          handleShareRoleplay={handleShareRoleplay}
          handleShareBot={handleShareBot}
          fileSizeLimit={config.fileSizeLimit}
          handleUploadBotExerciseImage={handleUploadBotExerciseImage}
          toggleMuteSound={toggleMuteSound}
          voices={
            course.learnLang
              ? config.languages[learnLang.toLowerCase() || "english"].Voices
              : []
          }
          learnLangCharacters={learnLangCharacters}
          forLangCharacters={specialCharacters}
          correctSound={correctSound}
          incorrectSound={incorrectSound}
          handleSpreadsheetSaveChanges={handleSpreadsheetSaveChanges}
          subscription={subscription}
          handleViewUserProfile={handleViewUserProfile}
          course={course}
          creatorSubscription={creatorSubscription}
          handleReportProblem={handleReportProblem}
          handleNotesImageUpload={handleNotesImageUpload}
          languageInformation={
            course.learnLang
              ? config.languages[learnLang.toLowerCase() || "english"]
              : null
          }
          handleOpenSelectClassroomDialog={handleOpenSelectClassroomDialog}
          isStudent={isStudent}
          teachClassrooms={teachClassrooms}
          uploadAudioAttempt={uploadAudioAttempt}
          trackRecommendedEvent={trackRecommendedEvent}
          authenticated={authenticated}
          handleSignIn={handleSignIn}
          validatePhraseRecognition={validatePhraseRecognition}
          verificationStatus={verificationStatus}
          languageTag={
            course.learnLang
              ? config.languages[
                learnLang.toLowerCase() || "english"
              ].Tag.split("-")[0]
              : "en"
          }
          siteLanguage={i18n.language}
          courseSettings={courseSettings?.[courseId]}
          handleDeleteTopicVideo={handleDeleteTopicVideo}
          loading={loading}
          videoUrl={courseSectionTopic?.videoUrl}
          pdfUrl={courseSectionTopic.pdf}
          pdfTitle={courseSectionTopic.courseName}
          handleDeletePDF={handleDeletePDF}
          handleToggleKeyboardTranslation={handleToggleKeyboardTranslation}
          makeChatGptApiRequest={makeChatGptApiRequest}
          parentClassroom={parentClassroom}
          topicGoal={topicGoal}
          isUserInternal={isUserInternal}
          isCommunityAccessRestricted={isCommunityAccessRestricted}
          isAdvancedStudent={classroomMember?.assignedLabels?.isAdvancedStudent}
          isNualangLiveEnabled={isNualangLiveEnabled}
          createdGame={createdGame}
          username={username}
          isChallengeModeStudent={
            classroomMember?.assignedLabels?.isChallengeModeStudent
          }
          isClassroomCreator={parentClassroom?.isCreator}
          isVideoChatEnabled={isVideoChatEnabled}
          isVideoChatEnabledInSettings={isVideoChatEnabledInSettings}
          handleCreatePhraseList={handleCreatePhraseList}
          handleUpdatePhraseList={handleUpdatePhraseList}
          handleDeletePhraseList={handleDeletePhraseList}
          handlePlayPhraseList={handlePlayPhraseList}
          handleEditPhraseList={handleEditPhraseList}
          handleDuplicatePhraseList={handleDuplicatePhraseList}
          avatars={avatars}
        />
      )}
      <SelectClassroom
        t={t}
        open={isSelectClassroomDialogOpen}
        handleClose={handleCloseSelectClassroomDialog}
        title={t("select_a_classroom")}
        type={"topic"}
        courseId={courseId}
        sectionId={sectionId}
        topicId={topicId}
        teachClassrooms={teachClassrooms.filter((classroom) =>
          classroom?.courses?.includes(courseId),
        )}
      />
    </>
  );
}

ViewTopic.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
  micPlaybackEnabled: PropTypes.bool,
  clearAttemptAudio: PropTypes.func,
  correctSound: PropTypes.object,
  incorrectSound: PropTypes.object,
  toggleMuteSound: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default ViewTopic;